import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { BuildVersion, UnversionedBuild } from '@/version'

/**
 * Cross-screen application state: connectivity, build version, updates.
 *
 * Per SPEC §13.3, stores hold state that outlives a screen. Server data for a
 * page is fetched by that page, not cached here — a global cache of event data
 * is how two screens end up disagreeing about whether someone is coming.
 */
export const useAppStore = defineStore('app', () => {
  const isOnline = ref(navigator.onLine)

  window.addEventListener('online', () => (isOnline.value = true))
  window.addEventListener('offline', () => (isOnline.value = false))

  /**
   * The build this client came from, and the one the server last reported.
   *
   * `clientVersion` is a ref rather than the imported constant so a test can
   * set it. Reading it straight from the bundle would make the only interesting
   * case — the two disagreeing — reachable only by rebuilding.
   */
  const clientVersion = ref(BuildVersion)
  const serverVersion = ref<string | null>(null)

  /** A newer worker has installed and is waiting for a navigation. */
  const workerWaiting = ref(false)

  /**
   * Screens with an unsaved edit in progress.
   *
   * SPEC §13.1 is explicit that nobody loses an in-progress RSVP to an update.
   * The prompt can never reload on its own, so the remaining risk is the prompt
   * itself: a banner that slides in under somebody's thumb while they are
   * choosing how many of them are coming is a banner they will tap. So a screen
   * that is mid-edit holds it back, and it appears when they are done.
   */
  const holds = ref(new Set<string>())

  function holdUpdatePrompt(reason: string): void {
    holds.value = new Set(holds.value).add(reason)
  }

  function releaseUpdatePrompt(reason: string): void {
    const next = new Set(holds.value)
    next.delete(reason)
    holds.value = next
  }

  /**
   * Whether the server is running a different build than this client.
   *
   * `local` on either side means somebody's development machine, where the two
   * never match and a permanent prompt would train people to ignore it.
   */
  const isStale = computed(() => {
    if (clientVersion.value === UnversionedBuild) return false
    if (serverVersion.value === null || serverVersion.value === UnversionedBuild) return false
    return serverVersion.value !== clientVersion.value
  })

  /** What the banner watches: something new exists, and now is a fine moment. */
  const updateAvailable = computed(
    () => (isStale.value || workerWaiting.value) && holds.value.size === 0,
  )

  /** Called with the `X-Build-Version` header of every API response. */
  function noteServerVersion(version: string | null): void {
    if (!version) return
    serverVersion.value = version
  }

  /** Called when the service worker reports a newer worker sitting in wait. */
  function noteWorkerWaiting(waiting: boolean): void {
    workerWaiting.value = waiting
  }

  return {
    isOnline,
    clientVersion,
    serverVersion,
    workerWaiting,
    isStale,
    updateAvailable,
    noteServerVersion,
    noteWorkerWaiting,
    holdUpdatePrompt,
    releaseUpdatePrompt,
  }
})
